// components/sections/AttachmentsSection.js
import React, { useEffect } from 'react';
import InteractiveField from '../common/InteractiveField';

/**
 * Компонент для раздела "9. Список приложений к настоящему Договору".
 * @param {object} data - Объект состояния для этого раздела.
 * @param {function} onChange - Функция для обновления состояния.
 * @param {object} contractData - Все данные договора для автоматического выбора актов.
 */
const AttachmentsSection = ({ data, onChange, contractData }) => {
  // Безопасная деструктуризация с фолбэками
  const {
    handoverAct = { enabled: true, title: 'Акт приема-передачи жилого помещения' },
    returnAct = { enabled: true, title: 'Акт возврата жилого помещения' },
    paymentAct = { enabled: false, title: 'Акт приема-передачи денежных средств' },
    egrnExtract = { enabled: false, number: '', date: '' },
    otherDocument = { enabled: false, name: '', number: '', date: '' }
  } = data;

  const handleNestedChange = (section, field, value) => {
    const newData = { ...data, [section]: { ...data[section], [field]: value } };
    onChange(newData);
  };

  // Автоматический выбор актов на основе других секций договора
  useEffect(() => {
    if (!contractData) return;

    const { 
      property = {}, 
      payment = {}, 
      usageConditions = {}, 
      rightsAndObligations = {},
      term = {}
    } = contractData;
    
    const { ownershipConfirmation = {} } = property;
    const { paymentMethod = {} } = payment;
    const { handoverOption = 'on_signing_day' } = rightsAndObligations;
    const { durationMonths = 11 } = term;

    let hasChanges = false;
    // Создаем глубокую копию нужных полей, чтобы не мутировать стейт напрямую
    const newData = { 
        ...data,
        handoverAct: { ...data.handoverAct },
        returnAct: { ...data.returnAct },
        paymentAct: { ...data.paymentAct },
        egrnExtract: { ...data.egrnExtract },
        otherDocument: { ...data.otherDocument }
    };

    const updateLocalData = (section, field, value) => {
        if (newData[section][field] !== value) {
            newData[section][field] = value;
            hasChanges = true;
        }
    };

    // 1. Акт приема-передачи жилого помещения - всегда включен
    if (!newData.handoverAct.enabled) {
      updateLocalData('handoverAct', 'enabled', true);
    }

    // 2. Акт возврата жилого помещения - всегда включен
    if (!newData.returnAct.enabled) {
      updateLocalData('returnAct', 'enabled', true);
    }

    // 3. Акт приема-передачи денежных средств - только при наличной оплате
    if (paymentMethod.type === 'cash') {
      if (!newData.paymentAct.enabled) {
        updateLocalData('paymentAct', 'enabled', true);
      }
    } else {
      if (newData.paymentAct.enabled) {
        updateLocalData('paymentAct', 'enabled', false);
      }
    }

    // 4. Выписка из ЕГРН - если выбрано подтверждение права собственности через ЕГРН
    if (ownershipConfirmation.type === 'egrn') {
      if (!newData.egrnExtract.enabled) {
        updateLocalData('egrnExtract', 'enabled', true);
      }
      
      if (ownershipConfirmation.details && newData.egrnExtract.name !== ownershipConfirmation.details) {
        updateLocalData('egrnExtract', 'name', ownershipConfirmation.details);
      }
    } else {
      if (newData.egrnExtract.enabled) {
        updateLocalData('egrnExtract', 'enabled', false);
      }
    }

    // 5. Иной документ - если выбрано подтверждение права собственности иным документом
    if (ownershipConfirmation.type === 'document') {
      if (!newData.otherDocument.enabled) {
        updateLocalData('otherDocument', 'enabled', true);
      }
      
      if (ownershipConfirmation.details && newData.otherDocument.name !== ownershipConfirmation.details) {
        updateLocalData('otherDocument', 'name', ownershipConfirmation.details);
      }
    } else {
      if (newData.otherDocument.enabled) {
        updateLocalData('otherDocument', 'enabled', false);
      }
    }

    if (hasChanges) {
        onChange(newData);
    }

  }, [
    contractData?.property?.ownershipConfirmation,
    contractData?.payment?.paymentMethod,
    contractData?.rightsAndObligations?.handoverOption,
    contractData?.term?.durationMonths,
    // data добавлено в зависимости, чтобы эффект видел актуальное состояние,
    // но hasChanges предотвратит бесконечный цикл
    data
  ]);

  // Счетчик для динамической нумерации
  let clauseCounter = 1;
  let appendixCounter = 1;

  // Отладочная информация
  console.log('AttachmentsSection render:', {
    otherDocument,
    egrnExtract,
    ownershipConfirmation: contractData?.property?.ownershipConfirmation,
    contractData: contractData?.property
  });

  // Формируем список активных приложений
  const activeAttachments = [];

  if (handoverAct.enabled) {
    activeAttachments.push({
      id: 'handover',
      number: clauseCounter++,
      title: handoverAct.title,
      appendix: appendixCounter++
    });
  }

  if (paymentAct.enabled) {
    activeAttachments.push({
      id: 'payment',
      number: clauseCounter++,
      title: paymentAct.title,
      appendix: appendixCounter++
    });
  }

  if (returnAct.enabled) {
    activeAttachments.push({
      id: 'return',
      number: clauseCounter++,
      title: returnAct.title,
      appendix: appendixCounter++
    });
  }

  if (egrnExtract.enabled) {
    // Объединяем стандартное название с тем, что ввел пользователь
    let extractTitle = 'Выписка из Единого государственного реестра недвижимости';
    if (egrnExtract.name) {
      extractTitle += ` ${egrnExtract.name}`;
    }
    
    console.log('AttachmentsSection: Creating EGRN extract with title:', extractTitle);
    
    activeAttachments.push({
      id: 'egrn',
      number: clauseCounter++,
      title: extractTitle,
      appendix: appendixCounter++
    });
  }

  if (otherDocument.enabled) {
    // Просто показываем то, что пользователь ввел
    const documentTitle = otherDocument.name || 'Иной документ';
    
    console.log('AttachmentsSection: Creating other document with title:', documentTitle);
    
    activeAttachments.push({
      id: 'other',
      number: clauseCounter++,
      title: documentTitle,
      appendix: appendixCounter++
    });
  }

  return (
    <div className="document-preview-editor attachments-section">
      <h2 className="special-section-title attachments-section__title">
        9. Список приложений к настоящему Договору
      </h2>

      <p className="document-preamble attachments-section__intro">
        Данные приложения к настоящему Договору, являются неотъемлемой его частью:
      </p>

      <div className="attachments-section__list">
        {activeAttachments.map((attachment) => (
          <p key={attachment.id} className="document-preamble attachments-section__item">
            <b>9.{attachment.number}.</b> {attachment.title} (Приложение № {attachment.appendix}).
          </p>
        ))}
      </div>
    </div>
  );
};

export default AttachmentsSection;
